import React from "react";
import { DriftingLetters, FloatingNotes } from "./BackgroundFx";

/**
 * Color palette per act. Each act's 3 dots share a hue so the audience
 * can quickly see which third of the show they're in. Colors are drawn
 * from the Fantasia ghost palette so the presentation and the
 * word-to-notes visuals rhyme.
 */
const ACT_COLORS = {
  1: "#F2D589", // gold
  2: "#FCA07A", // coral
  3: "#A774FF", // seafoam
};

/**
 * Progress dots for the presentation. Each of the 9 scenes gets one dot,
 * colored by act. Slides that share the same `scene_id` share a dot
 * (used by Act 2 Scene 3 which has both a QR sub-slide and an app
 * sub-slide — both light up the same dot).
 *
 * On an INTRO slide, no dot is "active" in the filled sense — instead,
 * that act's 3 dots are outlined in the act color to preview which
 * section we're about to enter.
 */
export function ProgressDots({ slides, currentSlideKey, onJump }) {
  // Collect one dot per unique scene_id, preserving slide order and
  // remembering the act each dot belongs to.
  const dots = [];
  const seen = new Set();
  for (const s of slides) {
    if (!s.is_scene) continue;
    const sid = s.scene_id || s.key;
    if (seen.has(sid)) continue;
    seen.add(sid);
    dots.push({ scene_id: sid, key: s.key, act: s.act || 1, label: s.scene_label || sid });
  }

  const current = slides.find((s) => s.key === currentSlideKey);
  const currentSceneId = current?.scene_id;
  const currentIdx = dots.findIndex((d) => d.scene_id === currentSceneId);
  const introAct = current && current.type === "intro" ? current.act : null;

  return (
    <div className="flex items-center gap-3" data-testid="slide-progress-dots">
      {dots.map((d, idx) => {
        const color = ACT_COLORS[d.act] || "#F5EFE3";
        const isCurrent = idx === currentIdx;
        const isPast = currentIdx > -1 && idx < currentIdx;
        const isIntroPreview = introAct === d.act;
        // Filled + gold-ish for the currently active scene.
        // Muted-filled for past scenes (in that act's color, dimmed).
        // Outlined in act color for the intro-preview state.
        // Dim gray for future scenes on non-intro slides.
        let bg;
        let borderStyle = "none";
        let size = isCurrent ? 14 : 10;
        if (isCurrent) {
          bg = color;
        } else if (isPast) {
          bg = `${color}80`; // 50% alpha
        } else if (isIntroPreview) {
          bg = "transparent";
          borderStyle = `2px solid ${color}`;
          size = 12;
        } else {
          bg = "rgba(255,255,255,0.16)";
        }
        return (
          <button
            key={d.scene_id}
            type="button"
            onClick={onJump ? () => onJump(d.key) : undefined}
            aria-label={d.label}
            data-testid={`slide-dot-${d.scene_id}`}
            style={{
              width: size,
              height: size,
              borderRadius: 999,
              backgroundColor: bg,
              border: borderStyle,
              transition: "all 220ms ease",
              cursor: onJump ? "pointer" : "default",
              padding: 0,
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * Base scaffolding used by all non-app slide types (title, intro, scene,
 * recap, conclusion). Renders the progress dots as a thin bar across the
 * top and provides a centered stage for slide content underneath.
 *
 * `hideDots` is used by the app-embedded slide types where we want the
 * live English/Hebrew display to take over completely (dots peek in from
 * the top only for hint, per Phase 4 spec).
 */
export function SlideShell({ slides, currentSlideKey, children, hideDots = false }) {
  return (
    <div className="relative w-full h-full min-h-screen flex flex-col" data-testid="slide-shell">
      {/* Toned-down home-page background — DriftingLetters + FloatingNotes
          + a subtle radial gradient wash. All three layers are muted via
          opacity so slide copy stays readable. `pointer-events-none` so
          the moderator can still click through if they've re-purposed
          the display as an admin control screen. */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity: 0.35 }}>
        <DriftingLetters count={22} />
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity: 0.3 }}>
        <FloatingNotes count={6} />
      </div>
      {/* Radial glow so the center of the slide feels lit while the
          background letters/notes stay dim toward the edges. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(232,196,138,0.05) 0%, rgba(5,2,17,0) 55%)",
        }}
      />
      {!hideDots && (
        <div
          className="absolute top-0 inset-x-0 z-30 flex justify-center pt-6"
          style={{ pointerEvents: "none" }}
        >
          <ProgressDots slides={slides} currentSlideKey={currentSlideKey} />
        </div>
      )}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-8 sm:px-16 lg:px-32 text-center">
        {children}
      </div>
    </div>
  );
}
